import { prop } from '@ipfabric/utils/fp/prop';
import { CloudInstanceDetailContent } from 'components/DeviceView/CloudConstructView/CloudInstanceDetailContent';
import { CloudInternetGatewayDetailContent } from 'components/DeviceView/CloudConstructView/CloudInternetGatewayDetailContent';
import { CloudNatGatewayDetailContent } from 'components/DeviceView/CloudConstructView/CloudNatGatewayDetailContent';
import { CloudNetworkDetailContent } from 'components/DeviceView/CloudConstructView/CloudNetworkDetailContent';
import { CloudNetworkInterfaceDetailContent } from 'components/DeviceView/CloudConstructView/CloudNetworkInterfaceDetailContent';
import { CloudPeeringDetailContent } from 'components/DeviceView/CloudConstructView/CloudPeeringDetailContent';
import { CloudSubnetDetailContent } from 'components/DeviceView/CloudConstructView/CloudSubnetDetailContent';
import type { DeviceViewEntityId } from 'components/DeviceView/deviceViewTypes';
import { useLocale } from 'containers/App/LocaleContext';
import { HostnameDetailContent } from 'containers/HostnameDetail/HostnameDetailContent';
import { type FC, memo } from 'react';
import { useDeviceViewContextSelectors } from '../model/DeviceViewModel';

interface Props {
  deviceViewEntityId: DeviceViewEntityId;
  disableSiteLink?: boolean;
}

const DETAIL_BOX_CLASS_NAME = 'box box-solid mb-0';
const FIRST_COLUMN_CLASS_NAME = 'w-0 whitespace-nowrap';
const SECOND_COLUMN_CLASS_NAME = 'w-full';
const DETAIL_TABLE_CLASS_NAME =
  'table table-bordered device-view-hostname-detail mb-0';

const BareDeviceViewContentDetailTabContent: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    tabData: prop('tabData'),
  });
  const msg = useLocale();
  const tabData = ctxProps.tabData[props.deviceViewEntityId];

  if (tabData?.cloudNetwork) {
    return (
      <div className={DETAIL_BOX_CLASS_NAME}>
        <CloudNetworkDetailContent
          firstColumnClassName={FIRST_COLUMN_CLASS_NAME}
          secondColumnClassName={SECOND_COLUMN_CLASS_NAME}
          tableClassName={DETAIL_TABLE_CLASS_NAME}
          tableData={tabData.cloudNetwork}
        />
      </div>
    );
  }

  if (tabData?.cloudNetworkInterface) {
    return (
      <div className={DETAIL_BOX_CLASS_NAME}>
        <CloudNetworkInterfaceDetailContent
          firstColumnClassName={FIRST_COLUMN_CLASS_NAME}
          secondColumnClassName={SECOND_COLUMN_CLASS_NAME}
          tableClassName={DETAIL_TABLE_CLASS_NAME}
          tableData={tabData.cloudNetworkInterface}
        />
      </div>
    );
  }

  if (tabData?.cloudSubnet) {
    return (
      <div className={DETAIL_BOX_CLASS_NAME}>
        <CloudSubnetDetailContent
          firstColumnClassName={FIRST_COLUMN_CLASS_NAME}
          secondColumnClassName={SECOND_COLUMN_CLASS_NAME}
          tableClassName={DETAIL_TABLE_CLASS_NAME}
          tableData={tabData.cloudSubnet}
        />
      </div>
    );
  }

  if (tabData?.cloudPeering) {
    return (
      <div className={DETAIL_BOX_CLASS_NAME}>
        <CloudPeeringDetailContent
          firstColumnClassName={FIRST_COLUMN_CLASS_NAME}
          secondColumnClassName={SECOND_COLUMN_CLASS_NAME}
          tableClassName={DETAIL_TABLE_CLASS_NAME}
          tableData={tabData.cloudPeering}
        />
      </div>
    );
  }

  if (tabData?.cloudInstance) {
    return (
      <div className={DETAIL_BOX_CLASS_NAME}>
        <CloudInstanceDetailContent
          firstColumnClassName={FIRST_COLUMN_CLASS_NAME}
          secondColumnClassName={SECOND_COLUMN_CLASS_NAME}
          tableClassName={DETAIL_TABLE_CLASS_NAME}
          tableData={tabData.cloudInstance}
        />
      </div>
    );
  }

  if (tabData?.cloudNatGateway) {
    return (
      <div className={DETAIL_BOX_CLASS_NAME}>
        <CloudNatGatewayDetailContent
          firstColumnClassName={FIRST_COLUMN_CLASS_NAME}
          secondColumnClassName={SECOND_COLUMN_CLASS_NAME}
          tableClassName={DETAIL_TABLE_CLASS_NAME}
          tableData={tabData.cloudNatGateway}
        />
      </div>
    );
  }

  if (tabData?.cloudInternetGateway) {
    return (
      <div className={DETAIL_BOX_CLASS_NAME}>
        <CloudInternetGatewayDetailContent
          firstColumnClassName={FIRST_COLUMN_CLASS_NAME}
          secondColumnClassName={SECOND_COLUMN_CLASS_NAME}
          tableClassName={DETAIL_TABLE_CLASS_NAME}
          tableData={tabData.cloudInternetGateway}
        />
      </div>
    );
  }

  return tabData?.device ? (
    <div className={DETAIL_BOX_CLASS_NAME}>
      <HostnameDetailContent
        disableSiteLink={props.disableSiteLink}
        firstColumnClassName={FIRST_COLUMN_CLASS_NAME}
        msg={msg.hostnameTableDetail.table}
        secondColumnClassName={SECOND_COLUMN_CLASS_NAME}
        tableClassName={DETAIL_TABLE_CLASS_NAME}
        tableData={tabData.device}
      />
    </div>
  ) : (
    <div className='flex flex-1 items-center justify-center'>
      <h4>{msg.deviceView.noDataAvailable}</h4>
    </div>
  );
};

export const DeviceViewContentDetailTabContent = memo(
  BareDeviceViewContentDetailTabContent,
);
